import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import { SuperHero } from "@bluebottlecoffee/design-system/components";
import { CanonicalHead } from "../../../../components/CanonicalHead";
import renderModule from "../../../../lib/render-module";
import { toSuperHeroProps } from "../../../../lib/transformers/super-hero";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import {
  getDefaultStaticPaths,
  getDefaultStaticPropsCafesPage,
} from "../../../../lib/utils";
import {
  AppDownload,
  CafesPage as CafesPageSchema,
  CarouselInlineCaption,
  EditorialFullBleed,
  TwoUp,
} from "../../../../lib/sanity-schema";
import { SanityKeyed } from "../../../../lib/sanity-keyed";
import {
  CafesPageBaseProps,
  CartBaseProps,
} from "../../../../lib/sanity/shared";
import { DereferencedCafeLocator } from "../../../../lib/transformers/cafe-locator";
import { useDoordashButton } from "../../../../lib/integrations/doordash/use-doordash-button";
import { resolveTabbedCollections } from "../../../../lib/resolve-tabbed-collections";

export type DereferencedCafesPage = Omit<CafesPageSchema, "modules"> & {
  modules?: Array<
    | SanityKeyed<AppDownload>
    | DereferencedCafeLocator
    | SanityKeyed<CarouselInlineCaption>
    | SanityKeyed<EditorialFullBleed>
    | SanityKeyed<TwoUp>
  >;
};

export const getStaticProps: GetStaticProps<CafesPageBaseProps> = async (
  context,
) => {
  const { props: defaultProps } = await getDefaultStaticPropsCafesPage(context);
  const { cafesPage, lang, region } = defaultProps;

  await resolveTabbedCollections(cafesPage?.modules, { lang, region });

  return {
    props: {
      cafesPage,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPaths;

function validate(data: CafesPageSchema) {
  if (!data.title) {
    throw Error("Cafes page must have a title.");
  }
}

export type CafesPageData = CartBaseProps & {
  cafesPage: DereferencedCafesPage;
};

const CafesLandingPage: FunctionComponent<CafesPageData> = ({
  cafesPage,
  ...layoutWrapperProps
}) => {
  const { region, lang, appPlayLinks, aria } = layoutWrapperProps;

  validate(cafesPage as CafesPageSchema);

  useDoordashButton();

  return (
    <>
      <CanonicalHead region={region} lang={lang} slug="cafes" />
      <LayoutWrapper
        {...{
          ...layoutWrapperProps,

          pageTitle: cafesPage.title[lang],
          metaTags: cafesPage.metaTags,
        }}
        isTransparent
      >
        {cafesPage.hero && cafesPage.hero?.length > 0 && (
          <SuperHero
            {...toSuperHeroProps(cafesPage.hero, aria, region, lang)}
          />
        )}
        {cafesPage.modules?.length > 0 &&
          cafesPage.modules.map((module) =>
            renderModule({
              module,
              region,
              lang,
              appPlayLinks,
              aria,
            }),
          )}
      </LayoutWrapper>
    </>
  );
};

export default CafesLandingPage;
